import { useCallback, useMemo } from 'react';

import { useUrlParams } from '~/hooks/useUrlParams';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  parsePage,
  parsePageSize,
  parseSort,
  patchList,
  showPager,
  toggleSort,
} from '~/lib/listParams';
import type { SortOrder } from '~/lib/listParams';

interface ListUrlConfig {
  /** URL keys of the list's filters; each is a plain string value. */
  filterKeys?: readonly string[];
  /** Fields the list can sort by; a `sort` outside this set is ignored. */
  sortFields?: readonly string[];
  pageSizes?: readonly number[];
  defaultPageSize?: number;
}

/** A list's whole view in the URL: `q`, `page`, `pageSize`, `sort`, `order` and its filters. */
export function useListUrlState({
  filterKeys = [],
  sortFields = [],
  pageSizes = PAGE_SIZE_OPTIONS,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: ListUrlConfig = {}) {
  const url = useUrlParams();

  const q = url.get('q') ?? '';
  const page = parsePage(url.get('page'));
  const pageSize = parsePageSize(url.get('pageSize'), pageSizes, defaultPageSize);
  const sort = parseSort(url.get('sort'), url.get('order'), sortFields);
  const filterSig = filterKeys.map((key) => url.get(key) ?? '').join('\u0000');
  const filters = useMemo(
    () => Object.fromEntries(filterKeys.map((key) => [key, url.get(key) ?? ''])),
    [filterSig],
  );
  const filterCount = Object.values(filters).filter(Boolean).length;

  const opts = { defaultPageSize };
  const setQ = (next: string) => url.set(patchList({ q: next }, opts));
  const setPage = (next: number) => url.set(patchList({ page: next }, opts));
  const setPageSize = (next: number) => url.set(patchList({ pageSize: next }, opts));
  const setFilters = (next: Record<string, string>) => url.set(patchList({ filters: next }, opts));
  const onSort = (field: string) => {
    const next = toggleSort(sort, field);
    url.set(patchList({ sort: next.sort, order: next.order as SortOrder }, opts));
  };
  const clear = useCallback(
    () =>
      url.set({
        q: null,
        page: null,
        ...Object.fromEntries(filterKeys.map((key) => [key, null])),
      }),
    [url, filterKeys],
  );

  return {
    q,
    page,
    pageSize,
    pageSizes,
    sort: sort?.sort ?? null,
    order: (sort?.order ?? null) as SortOrder | null,
    filters,
    filterCount,
    setQ,
    setPage,
    setPageSize,
    setFilters,
    onSort,
    clear,
    showPager: (totalRows: number) => showPager(totalRows, pageSize),
  };
}
